import {
    Breakpoints,
    Text,
    TextAlignment,
    TextTag,
    TokenTextAppearance,
} from '@volkswagen-onehub/components-core'
import styled from 'styled-components'
import React, {useEffect, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {boxedContainer} from '../../../assets/css-vars/gridUtils'
import {QueryParams} from '../../../common/configuration'
import SingleModelInterface, {
    Promotion,
} from '../../../types/singleModelInterface'
import addTrailingSlash from '../../../utils/url/addTrailingSlash'
import {RootState} from '../../rootReducer'
import {
    AppDispatch,
    FetchData,
    fetchAllModelsWithActivePromotions,
} from '../../thunk'
import {SingleModel} from './singleModel'
import {BackendEndpoints} from '../../../utils/url/BackendEndpoints'
import {useTranslation} from 'react-i18next'
import Brands from '../../../utils/brand/brand'
import {Global, PromotionType} from '../../../shared/variables'
import {Loading} from '../../../common/components/general/loading'
import HorizontalRFQFixedBanner from '../ui/HorizontalRFQFixedBanner'
import {openCouponLayer} from '../layers/launchers/CouponRequestLayerHandle'
import {isMobile} from '../../../shared/utilities'
import {setFormCycleID} from '../../../features/userData/userDataSlice'
import {generateRandomString} from '../../../utils/tracking/tracking-utils/generateRandomString'

const ListingGridContainer = styled.div`
    margin-top: 0;
    margin-bottom: 56px;
    background-color: ${({theme}) => `${theme.colors.background.primary}`};
    padding: 44px 15px;

    @media (min-width: ${Breakpoints.b560}px) {
        padding-inline: 0;
    }
`

const ListingGrid = styled.ul`
    ${boxedContainer};
    display: grid;
    grid-template-columns: 1fr;
    gap: 44px 16px;
    list-style: none;
    padding: 0;
    margin-top: 0;
    margin-bottom: 0;

    @media (min-width: ${Breakpoints.b560}px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (min-width: ${Breakpoints.b960}px) {
        grid-template-columns: repeat(3, 1fr);
    }

    @media (min-width: ${Breakpoints.b1280}px) {
        row-gap: 56px;
    }

    @media (min-width: ${Breakpoints.b1600}px) {
        padding-right: 0;
        padding-left: 0;
    }
`

function getFirstActivePromotion(model: SingleModelInterface): Promotion {
    return model.related_promotions[0]
}

function sortFilteredModels(
    filteredModels: SingleModelInterface[]
): SingleModelInterface[] {
    const getSortingWeight = (model: SingleModelInterface): number => {
        const firstPromo = getFirstActivePromotion(model)

        if (firstPromo?.type === PromotionType.LOCAL) {
            return 1
        }
        if (
            firstPromo?.type === PromotionType.NATIONAL &&
            firstPromo.accepted_by_dealer === 1
        ) {
            return 2
        }
        if (
            firstPromo?.type === PromotionType.NATIONAL &&
            firstPromo.accepted_by_dealer === 0
        ) {
            return 3
        }

        return 4
    }

    return [...filteredModels].sort((modelA, modelB) => {
        const weightA = getSortingWeight(modelA)
        const weightB = getSortingWeight(modelB)

        return weightA - weightB
    })
}

export function ListingModels(): React.JSX.Element {
    // tslint:disable-next-line:no-unnecessary-type-assertion
    const dispatch = useDispatch() as AppDispatch
    const {t} = useTranslation('app')
    const backendUrl = useSelector(
        (state: RootState) => state.general.backendUrl
    )
    const apiQueryParams = useSelector(
        (state: RootState) => state.general.apiQueryParams
    )
    const previewAPIToken = useSelector(
        (state: RootState) => state.general.previewAPIToken
    )
    const brand = useSelector((state: RootState) => state.general.brand)

    const dealerCode = useSelector(
        (state: RootState) => state.dealerData.dealerCode
    )
    const dealerName = useSelector(
        (state: RootState) => state.dealerData.dealerName
    )
    const filteredModels = useSelector(
        (state: RootState) => state.general.filteredModels
    )
    const showRFQForm = useSelector(
        (state: RootState) => state.general.showRFQForm
    )

    const [modelsLoading, setModelsLoading] = useState(true)
    const loading = useSelector((state: RootState) => state.general.loading)
    const gridRef = useRef<HTMLUListElement | null>(null)
    const [isGridBottomVisible, setIsGridBottomVisible] = useState(false)
    const [tagsWrapped, setTagsWrapped] = useState(false)

    useEffect(() => {
        if (backendUrl.length) {
            setModelsLoading(true)
            let modelsEndpointUrl =
                addTrailingSlash(backendUrl) + BackendEndpoints.CARS
            if (dealerCode) {
                modelsEndpointUrl =
                    addTrailingSlash(
                        addTrailingSlash(modelsEndpointUrl) +
                            BackendEndpoints.DEALER
                    ) + dealerCode
            }

            const allModelsCall: FetchData = {
                ENDPOINT: modelsEndpointUrl,
                TOKEN: '',
            }

            if (apiQueryParams && previewAPIToken) {
                allModelsCall.ENDPOINT = `${allModelsCall.ENDPOINT}?${QueryParams.PREVIEW_DATE}=${apiQueryParams}`
                allModelsCall.TOKEN = previewAPIToken
            }

            dispatch(fetchAllModelsWithActivePromotions(allModelsCall)).then(
                () => {
                    setModelsLoading(false)
                }
            )
        }
    }, [backendUrl, apiQueryParams, previewAPIToken, dealerCode])

    useEffect(() => {
        const checkTagWrapping = () => {
            if (!gridRef.current) {
                return
            }

            const tagContainers = gridRef.current.querySelectorAll(
                '[data-tags-container]'
            )

            let hasWrapped = false

            tagContainers.forEach((container) => {
                const height = container.getBoundingClientRect().height
                if (height > 28) {
                    hasWrapped = true
                }
            })

            setTagsWrapped(hasWrapped)
        }

        checkTagWrapping()

        const resizeObserver = new ResizeObserver(checkTagWrapping)
        if (gridRef.current) {
            resizeObserver.observe(gridRef.current)
        }

        const mutationObserver = new MutationObserver(checkTagWrapping)
        if (gridRef.current) {
            mutationObserver.observe(gridRef.current, {
                childList: true,
                subtree: true,
            })
        }

        return () => {
            resizeObserver.disconnect()
            mutationObserver.disconnect()
        }
    }, [filteredModels])

    useEffect(() => {
        const threshold = isMobile ? 0.05 : 0.1

        const observer = new IntersectionObserver(
            ([entry]) => setIsGridBottomVisible(entry.isIntersecting),
            {
                root: null,
                threshold,
            }
        )

        if (gridRef.current) {
            observer.observe(gridRef.current)
        }

        return () => observer.disconnect()
    }, [filteredModels.length])

    return (
        <>
            {(filteredModels.length || modelsLoading || loading) && (
                <ListingGridContainer>
                    {filteredModels.length ? (
                        <>
                            <ListingGrid ref={gridRef}>
                                {sortFilteredModels(filteredModels).map(
                                    (
                                        model: SingleModelInterface
                                    ): React.JSX.Element | undefined => {
                                        if (!model.related_promotions.length) {
                                            return undefined
                                        }

                                        const firstActivePromotion =
                                            getFirstActivePromotion(model)

                                        return (
                                            <SingleModel
                                                key={model.id}
                                                carImage={model.image}
                                                carName={model.name}
                                                backgroundColor={
                                                    model.stage_color
                                                }
                                                promoText={{
                                                    price_prefix:
                                                        firstActivePromotion.price_prefix,
                                                    price: firstActivePromotion.price,
                                                    price_suffix:
                                                        firstActivePromotion.price_suffix,
                                                    promotional_package:
                                                        firstActivePromotion.promotional_package,
                                                }}
                                                promoType={
                                                    firstActivePromotion.title
                                                }
                                                promoIsHighlighted={
                                                    model.is_highlighted
                                                }
                                                promoLink={model.slug}
                                                promotional_equipment={
                                                    model.promotional_equipment
                                                }
                                                isDealerSelected={
                                                    dealerCode !== ''
                                                }
                                                dealerName={dealerName}
                                                promoScope={
                                                    firstActivePromotion.type
                                                }
                                                isPromoAvailable={
                                                    firstActivePromotion.accepted_by_dealer
                                                }
                                                tag_label_1={
                                                    firstActivePromotion.tag_label_1
                                                }
                                                tag_label_2={
                                                    firstActivePromotion.tag_label_2
                                                }
                                                fuels={
                                                    firstActivePromotion.fuels
                                                }
                                                tagsWrapped={tagsWrapped}
                                            />
                                        )
                                    }
                                )}
                            </ListingGrid>
                            {isGridBottomVisible && showRFQForm && (
                                <HorizontalRFQFixedBanner
                                    onClick={() => {
                                        dispatch(
                                            setFormCycleID(
                                                generateRandomString(
                                                    Global.CYCLE_ID_LENGTH
                                                )
                                            )
                                        )
                                        openCouponLayer()
                                    }}
                                    ctaLabel={t(
                                        'cta.singlePromotion.requestQuote'
                                    )}
                                />
                            )}
                        </>
                    ) : (
                        !modelsLoading &&
                        brand === Brands.VW && (
                            <>
                                <div>{modelsLoading}</div>
                                <Text
                                    tag={TextTag.p}
                                    textAlign={TextAlignment.center}
                                    appearance={TokenTextAppearance.headline300}
                                >
                                    {t('listing.noAvailablePromotions')}
                                </Text>
                            </>
                        )
                    )}

                    {modelsLoading || loading ? <Loading /> : undefined}
                </ListingGridContainer>
            )}
        </>
    )
}
